import { RefObject, useEffect } from "react";
import { FocusableElement, tabbable } from "tabbable";

export type InitialFocusOptions = {
  /**
   * Function to pick the initial focus target, given an array of focusable elements
   */
  computeFocus?: (focusables: FocusableElement[]) => FocusableElement;
};

/**
 * Hook to set initial focus for a dialog (e.g. Dialog, Drawer) when it is opened.
 * @param dialogRef - Ref to the dialog element
 * @param options - Options for the hook
 * @param options.computeFocus - Function to pick the initial focus target, given an array of focusable elements
 */
export const useInitialFocus = (
  /**
   * Ref to the dialog element
   */
  dialogRef: RefObject<HTMLDialogElement>,
  { computeFocus }: InitialFocusOptions,
) => {
  useEffect(() => {
    if (!dialogRef.current) {
      return () => {};
    }
    // Create a MutationObserver to check if dialogRef.current is open or closed
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.attributeName === "open" &&
          mutation.target === dialogRef.current &&
          mutation.target instanceof HTMLDialogElement &&
          mutation.target.open === true
        ) {
          const focusables = tabbable(dialogRef.current);
          const target = computeFocus?.(focusables) || focusables[0];
          target?.focus();
        }
      });
    });
    observer.observe(dialogRef.current, {
      attributes: true,
      attributeFilter: ["open"],
    });
    return () => {
      observer.disconnect();
    };
  }, [dialogRef, computeFocus]);
};
